import Button from "../../components/Button";
import TextInput from "../../components/TextInput";
import type { ParcoursDateDto } from "../../../api/parcoursApi";

interface Props {
  value: ParcoursDateDto[];
  onChange: (next: ParcoursDateDto[]) => void;
}

export default function DaysEditor({ value, onChange }: Props) {
  const update = (index: number, patch: Partial<ParcoursDateDto>) => {
    const next = value.map((d, i) => (i === index ? { ...d, ...patch } : d));
    onChange(next);
  };

  const remove = (index: number) => onChange(value.filter((_, i) => i !== index));

  const add = () => {
    const today = new Date().toISOString().slice(0, 10);
    onChange([...value, { date: today, startTime: null, endDate: null, endTime: null }]);
  };

  return (
    <div className="bo-stack-tight">
      {value.length === 0 && (
        <p style={{ color: "var(--ink-mute)", fontSize: 13, margin: 0 }}>
          Aucun créneau pour le moment. Ajoutez les dates auxquelles le parcours est ouvert
          (un créneau peut couvrir un seul jour ou plusieurs jours).
        </p>
      )}
      {value.map((slot, i) => (
        <div
          key={i}
          style={{
            padding: "var(--sp-3)",
            background: "var(--paper)",
            border: "1px solid var(--line)",
            borderRadius: "var(--radius-s)",
            display: "flex",
            flexDirection: "column",
            gap: "var(--sp-3)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "var(--sp-3)",
            }}
          >
            <span style={{ fontFamily: "var(--font-display)", fontSize: 14, textTransform: "uppercase" }}>
              Créneau {i + 1}
            </span>
            <Button type="button" variant="ghost" onClick={() => remove(i)}>
              × Retirer
            </Button>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "var(--sp-4)",
            }}
          >
            <fieldset className="bo-fieldset" style={{ margin: 0 }}>
              <legend>Début</legend>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.4fr 1fr",
                  gap: "var(--sp-3)",
                }}
              >
                <Field label="Date" required>
                  <TextInput
                    type="date"
                    value={slot.date ?? ""}
                    onChange={(e) => update(i, { date: e.target.value })}
                    required
                  />
                </Field>
                <Field label="Heure" hint="Optionnel">
                  <TextInput
                    type="time"
                    value={slot.startTime ?? ""}
                    onChange={(e) => update(i, { startTime: e.target.value || null })}
                  />
                </Field>
              </div>
            </fieldset>

            <fieldset className="bo-fieldset" style={{ margin: 0 }}>
              <legend>Fin</legend>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.4fr 1fr",
                  gap: "var(--sp-3)",
                }}
              >
                <Field label="Date" hint="Optionnel — vide ⇒ même jour">
                  <TextInput
                    type="date"
                    min={slot.date ?? undefined}
                    value={slot.endDate ?? ""}
                    onChange={(e) => update(i, { endDate: e.target.value || null })}
                  />
                </Field>
                <Field label="Heure" hint="Optionnel">
                  <TextInput
                    type="time"
                    value={slot.endTime ?? ""}
                    onChange={(e) => update(i, { endTime: e.target.value || null })}
                  />
                </Field>
              </div>
            </fieldset>
          </div>
        </div>
      ))}
      <div>
        <Button type="button" variant="secondary" onClick={add}>
          + Ajouter un créneau
        </Button>
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-soft)" }}>
        {label}
        {required && <span style={{ color: "var(--red)", marginLeft: 4 }}>*</span>}
      </span>
      {children}
      {hint && <span style={{ fontSize: 11, color: "var(--ink-faint)" }}>{hint}</span>}
    </div>
  );
}
